/**
 * One drawing of a conditional allocation, shared by the two pages that need one.
 *
 * Allocation Analysis draws the branches that can reach ONE ticker, from a saved strategy. The
 * Visualizer draws a whole definition — a Composer strategy or a `vrk-portfolio/v1` document —
 * that may never be saved at all. Same node vocabulary, so the same component: `if`, `weight`,
 * `filter`, `group` and a labelled `node` fallback.
 *
 * The Visualizer's arrival added `group` and `weight_pct` and gave the fallback its label back;
 * none of the three changes what Allocation Analysis renders, because its extractor emits neither
 * `group` nor `weight_pct`, and a `node` that used to print the word "node" now prints what the
 * node actually is.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, GitBranch, Layers, Filter, Folder } from 'lucide-react';

/**
 * The share of its parent this node was given, when the parent named one.
 *
 * An asset carries its own share inside its pill. A weighted *subtree* has no pill, so
 * `weight-specified` hangs the percentage here — and 60/40 between two branches is usually the
 * most consequential number on the screen.
 */
interface Weighted {
  weight_pct?: number | null;
}

export type LogicNode =
  | ({
      type: 'if';
      condition: string;
      symbol_in_then: boolean;
      symbol_in_else: boolean;
      then: LogicNode | null;
      else: LogicNode | null;
    } & Weighted)
  | ({
      type: 'weight';
      label: string;
      assets: Array<{ ticker: string; weight: number | null }>;
      children: LogicNode[];
      symbol_present: boolean;
    } & Weighted)
  | ({
      type: 'filter';
      sort_by: string;
      select: string;
      assets: Array<{ ticker: string }>;
      symbol_present: boolean;
    } & Weighted)
  /** A named container in the definition — a Composer `group`, or an anonymous sequence. */
  | ({
      type: 'group';
      label: string;
      children: LogicNode[];
      symbol_present: boolean;
    } & Weighted)
  | ({ type: 'node'; label: string; symbol_present: boolean } & Weighted)
  | { type: string; [key: string]: unknown };

interface Props {
  node: LogicNode;
  symbol: string;
  depth?: number;
  /**
   * Nodes at or below this depth start expanded; deeper ones start folded.
   *
   * `Infinity` — the default, and what Allocation Analysis gets — means everything starts open,
   * which is right for a tree filtered down to one ticker's branches.
   *
   * The Visualizer passes a real number because its trees are not filtered. A 30 MB strategy draws
   * 3,661 nodes and 1,598 asset pills fifty levels deep; building that DOM eagerly costs seconds
   * before anyone has looked at it, and a collapsed branch renders no children at all. Reading an
   * if-cascade is a drill anyway — you follow the ELSE down, one level at a time.
   */
  defaultExpandedDepth?: number;
}

function AssetPill({ ticker, symbol, weight }: { ticker: string; symbol: string; weight?: number | null }) {
  const isTarget = ticker.toUpperCase() === symbol.toUpperCase();
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${
        isTarget
          ? 'bg-emerald-50 border-emerald-400 text-emerald-800 ring-1 ring-emerald-300'
          : 'bg-gray-50 border-gray-200 text-gray-600'
      }`}
    >
      {ticker}
      {weight != null && (
        <span className={`font-normal ${isTarget ? 'text-emerald-600' : 'text-gray-400'}`}>
          {weight}%
        </span>
      )}
    </span>
  );
}

/** The share a weighted subtree was given by its parent. Absent on every unweighted node. */
function WeightBadge({ pct }: { pct?: number | null }) {
  if (pct == null) return null;
  return (
    <span className="shrink-0 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
      {pct}%
    </span>
  );
}

export function LogicTree({
  node,
  symbol,
  depth = 0,
  defaultExpandedDepth = Infinity,
}: Props) {
  const [expanded, setExpanded] = useState(depth < defaultExpandedDepth);
  const weightPct = (node as Weighted).weight_pct;

  if (node.type === 'if') {
    const n = node as Extract<LogicNode, { type: 'if' }>;
    return (
      <div className={depth > 0 ? 'border-l-2 border-gray-100 pl-3 mt-2' : ''}>
        {/* Condition row */}
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex items-start gap-1.5 w-full text-left group"
        >
          <span className="mt-0.5 text-gray-400 group-hover:text-gray-600 shrink-0">
            {expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </span>
          <GitBranch className="w-3.5 h-3.5 mt-0.5 text-indigo-400 shrink-0" />
          <span className="text-xs">
            <span className="font-bold text-indigo-700 mr-1.5">IF</span>
            <span className="font-mono text-gray-700 break-all">{n.condition}</span>
          </span>
          <WeightBadge pct={weightPct} />
        </button>

        {expanded && (
          <div className="mt-1.5 space-y-2">
            {/* THEN branch */}
            <div className="pl-5">
              <span
                className={`inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded mb-1 ${
                  n.symbol_in_then
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {n.symbol_in_then ? '✓ ' : ''}THEN
              </span>
              {n.then ? (
                <LogicTree node={n.then} symbol={symbol} depth={depth + 1} defaultExpandedDepth={defaultExpandedDepth} />
              ) : (
                <span className="text-xs text-gray-400 italic ml-1">—</span>
              )}
            </div>

            {/* ELSE branch */}
            {(n.else || n.symbol_in_else !== undefined) && (
              <div className="pl-5">
                <span
                  className={`inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded mb-1 ${
                    n.symbol_in_else
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {n.symbol_in_else ? '✓ ' : ''}ELSE
                </span>
                {n.else ? (
                  <LogicTree node={n.else} symbol={symbol} depth={depth + 1} defaultExpandedDepth={defaultExpandedDepth} />
                ) : (
                  <span className="text-xs text-gray-400 italic ml-1">—</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (node.type === 'weight') {
    const n = node as Extract<LogicNode, { type: 'weight' }>;
    return (
      <div className={depth > 0 ? 'pl-1 mt-1' : ''}>
        <div className="flex items-start gap-1.5 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-gray-500 font-medium shrink-0 mt-0.5">
            <Layers className="w-3 h-3" />
            {n.label}:
          </span>
          <WeightBadge pct={weightPct} />
          <div className="flex flex-wrap gap-1">
            {n.assets.map((a) => (
              <AssetPill key={a.ticker} ticker={a.ticker} symbol={symbol} weight={a.weight} />
            ))}
          </div>
        </div>
        {n.children.length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {n.children.map((child, i) => (
              <LogicTree
                key={i}
                node={child}
                symbol={symbol}
                depth={depth + 1}
                defaultExpandedDepth={defaultExpandedDepth}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (node.type === 'filter') {
    const n = node as Extract<LogicNode, { type: 'filter' }>;
    return (
      <div className={depth > 0 ? 'pl-1 mt-1' : ''}>
        <div className="flex items-start gap-1.5 flex-wrap text-xs">
          <span className="flex items-center gap-1 text-purple-700 font-semibold mt-0.5 shrink-0">
            <Filter className="w-3 h-3" />
            FILTER
          </span>
          {n.sort_by && (
            <span className="text-gray-500 mt-0.5">
              sort by{' '}
              <span className="font-mono text-gray-800 bg-gray-100 px-1 rounded">
                {n.sort_by}
              </span>
            </span>
          )}
          {n.select && (
            <span className="text-gray-500 mt-0.5">
              select{' '}
              <span className="font-mono text-gray-800 bg-gray-100 px-1 rounded">
                {n.select}
              </span>
            </span>
          )}
        </div>
        {n.assets.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
            {n.assets.map((a) => (
              <AssetPill key={a.ticker} ticker={a.ticker} symbol={symbol} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (node.type === 'group') {
    const n = node as Extract<LogicNode, { type: 'group' }>;
    return (
      <div className={depth > 0 ? 'border-l-2 border-gray-100 pl-3 mt-2' : ''}>
        {/* Collapsible, like `if`, and for the same reason: a real definition nests groups a dozen
            deep, and folding one away is how a reader gets back to the level they care about. */}
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex items-start gap-1.5 w-full text-left group"
        >
          <span className="mt-0.5 text-gray-400 group-hover:text-gray-600 shrink-0">
            {expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </span>
          <Folder className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
          <span className="text-xs font-medium text-slate-600 break-all">
            {/* An anonymous sequence — several nodes side by side with no name in the source. */}
            {n.label || <span className="italic text-gray-400">group</span>}
          </span>
          <WeightBadge pct={weightPct} />
        </button>
        {expanded && (
          <div className="mt-1.5 space-y-1.5 pl-5">
            {n.children.map((child, i) => (
              <LogicTree
                key={i}
                node={child}
                symbol={symbol}
                depth={depth + 1}
                defaultExpandedDepth={defaultExpandedDepth}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Anything this vocabulary does not name, drawn as itself. `label` is what the node IS — the
  // Composer function, or a truncation notice — and printing the literal word "node" instead, as
  // this used to, told a reader nothing about their own strategy.
  const fallback = node as { type: string; label?: string };
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-400 font-mono pl-1 mt-0.5">
      <span>{fallback.label || fallback.type}</span>
      <WeightBadge pct={weightPct} />
    </div>
  );
}
